import { Switch } from "@headlessui/react";
import cx from "classnames";
import { ReactNode } from "react";

const getContainerSizeClass = (size: string) => {
  switch (size) {
    case "small":
      return "h-5 w-10";
    case "normal":
      return "h-6 w-11";
    case "large":
      return "h-7 w-12";
    default:
      return "h-6 w-11";
  }
};

const getSwitchSizeClass = (size: string) => {
  switch (size) {
    case "small":
      return "h-4 w-4";
    case "normal":
      return "h-5 w-5";
    case "large":
      return "h-6 w-6";
    default:
      return "h-5 w-5";
  }
};

interface SwitchInputProps {
  className?: string;
  checked: boolean;
  description?: ReactNode;
  descriptionClassName?: string;
  descriptionId?: string;
  disabled?: boolean;
  label?: string;
  labelDirection?: "right" | "left";
  name: string;
  onChange: (checked: boolean) => void;
  size?: "small" | "normal" | "large";
}

const SwitchInput = ({
  className,
  checked,
  description,
  descriptionClassName,
  descriptionId,
  disabled,
  label,
  labelDirection = "left",
  name,
  onChange,
  size = "normal"
}: SwitchInputProps) => {
  const switchInput = (
    <div
      className={cx(
        "text-gray-600 text-sm font-semibold",
        "flex items-center gap-2",
        className
      )}
      data-testid={`${name}-switch`}
    >
      {label && labelDirection === "left" && <span>{label}</span>}
      <Switch
        disabled={disabled}
        name={`${name}-switch`}
        checked={checked}
        onChange={onChange}
        className={cx(
          checked ? "bg-teal-300" : "bg-gray-400",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          "inline-flex items-center rounded-full border-0",
          getContainerSizeClass(size)
        )}
      >
        <span className="sr-only">{label}</span>
        <span
          className={cx(
            checked ? "translate-x-4" : "-translate-x-1",
            "inline-block transform rounded-full bg-white transition",
            getSwitchSizeClass(size)
          )}
        />
      </Switch>
      {label && labelDirection === "right" && <span>{label}</span>}
    </div>
  );

  if (!description) {
    return switchInput;
  }

  return (
    <Switch.Group>
      {switchInput}
      <Switch.Description
        id={descriptionId}
        className={descriptionClassName}
      >
        {description}
      </Switch.Description>
    </Switch.Group>
  );
};

export default SwitchInput;
